import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, EASE } from "./Reveal";
import { STUDIO, EVENT_TYPES } from "../data/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = {
  name: "",
  phone: "",
  wedding_date: "",
  venue_city: "",
  event_type: EVENT_TYPES[0],
  message: "",
};

export default function BookingForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please tell us your name";
    const digits = form.phone.replace(/\D/g, "");
    if (!digits || digits.length < 10) errs.phone = "Please enter a valid phone number";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success("Enquiry received! We will call you back soon.");
    } catch (err) {
      toast.error("Something went wrong. Please call or WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setDone(false);
  };

  return (
    <section id="book" data-testid="booking-section" className="py-20 sm:py-28 lg:py-32 bg-ink-soft/30 border-t border-white/[0.05]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
        {/* Left — invite + direct contacts */}
        <div>
          <Reveal className="flex items-center gap-4 mb-6">
            <span className="h-px w-10 bg-gold/50" />
            <span className="eyebrow">Book Your Date</span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight text-ivory mb-5">
              Let's tell your story, <span className="italic text-gold">together</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-base sm:text-lg font-light text-ivory-dim leading-relaxed mb-9">
              Share a few details and we'll call you back with a plan crafted around your
              celebration. Dates fill fast in wedding season — <em>shubh ghadi</em> wait for no one.
            </p>
          </Reveal>

          <div className="space-y-4">
            {STUDIO.contacts.map((c, i) => (
              <Reveal key={c.tel} delay={0.25 + i * 0.1}>
                <a
                  href={c.tel}
                  data-testid={`tap-to-call-${c.tel.replace(/\D/g, "").slice(-4)}`}
                  className="flex items-center justify-between border border-white/[0.08] bg-ink-soft/60 px-6 py-5 hover:border-gold/50 transition-colors duration-300 group"
                >
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.22em] text-ivory-mute mb-1">
                      {c.name} · {c.role}
                    </span>
                    <span className="block font-display text-xl sm:text-2xl text-ivory group-hover:text-gold transition-colors">
                      {c.phone}
                    </span>
                  </span>
                  <span className="w-11 h-11 border border-gold/30 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-ink transition-all">
                    <Phone className="w-4.5 h-4.5 w-5 h-5" />
                  </span>
                </a>
              </Reveal>
            ))}

            <Reveal delay={0.45}>
              <a
                href={STUDIO.whatsapp}
                target="_blank"
                rel="noreferrer"
                data-testid="booking-whatsapp-btn"
                className="flex items-center justify-between border border-[#25D366]/40 bg-[#25D366]/[0.07] px-6 py-5 hover:bg-[#25D366]/[0.15] transition-colors duration-300 group"
              >
                <span>
                  <span className="block text-[11px] uppercase tracking-[0.22em] text-ivory-mute mb-1">
                    Prefer chatting?
                  </span>
                  <span className="block font-display text-xl sm:text-2xl text-ivory group-hover:text-[#25D366] transition-colors">
                    WhatsApp us directly
                  </span>
                </span>
                <MessageCircle className="w-6 h-6 text-[#25D366]" />
              </a>
            </Reveal>

            <Reveal delay={0.55} className="flex items-center gap-3 text-sm text-ivory-mute pt-2">
              <MapPin className="w-4 h-4 text-gold shrink-0" />
              {STUDIO.serviceAreas}
            </Reveal>
          </div>
        </div>

        {/* Right — the form */}
        <Reveal delay={0.15}>
          <div className="border border-white/[0.08] bg-ink-soft/60 p-6 sm:p-9 lg:p-10" data-testid="booking-form-card">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="success"
                  data-testid="enquiry-success-card"
                  className="py-14 flex flex-col items-center text-center"
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, ease: EASE }}
                >
                  <span className="w-20 h-20 rounded-full border border-gold/50 flex items-center justify-center text-gold mb-7">
                    <CheckCircle2 className="w-9 h-9" strokeWidth={1.4} />
                  </span>
                  <h3 className="font-display text-3xl text-ivory mb-3">Dhanyavaad, {form.name.split(" ")[0]}!</h3>
                  <p className="text-ivory-dim text-sm leading-relaxed max-w-sm mb-8">
                    Your enquiry is safely with us. Mohit or Vinod Bhai will call you back
                    shortly to weave your dates into our calendar.
                  </p>
                  <button
                    data-testid="enquiry-success-reset-btn"
                    onClick={reset}
                    className="text-[12px] uppercase tracking-[0.2em] text-gold border-b border-gold/40 pb-1 hover:text-gold-hover transition-colors"
                  >
                    Send another enquiry
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="booking-form"
                  onSubmit={submit}
                  noValidate
                  className="space-y-5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -12 }}
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="field-label" htmlFor="enq-name">Your Name *</label>
                      <input
                        id="enq-name"
                        data-testid="enquiry-input-name"
                        className="field"
                        placeholder="Aarav Shah"
                        value={form.name}
                        onChange={set("name")}
                      />
                      {errors.name && <p data-testid="enquiry-error-name" className="text-red-400 text-xs mt-1.5">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="field-label" htmlFor="enq-phone">Phone / WhatsApp *</label>
                      <input
                        id="enq-phone"
                        data-testid="enquiry-input-phone"
                        className="field"
                        placeholder="98765 43210"
                        inputMode="tel"
                        value={form.phone}
                        onChange={set("phone")}
                      />
                      {errors.phone && <p data-testid="enquiry-error-phone" className="text-red-400 text-xs mt-1.5">{errors.phone}</p>}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="field-label" htmlFor="enq-date">Wedding Date</label>
                      <input
                        id="enq-date"
                        data-testid="enquiry-input-date"
                        type="date"
                        className="field"
                        value={form.wedding_date}
                        onChange={set("wedding_date")}
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="enq-city">Venue / City</label>
                      <input
                        id="enq-city"
                        data-testid="enquiry-input-city"
                        className="field"
                        placeholder="Keshod, or venue name"
                        value={form.venue_city}
                        onChange={set("venue_city")}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="field-label" htmlFor="enq-type">Event Type</label>
                    <select
                      id="enq-type"
                      data-testid="enquiry-input-type"
                      className="field"
                      value={form.event_type}
                      onChange={set("event_type")}
                    >
                      {EVENT_TYPES.map((t) => (
                        <option key={t} value={t} className="bg-ink text-ivory">
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="field-label" htmlFor="enq-msg">Tell us about your celebration</label>
                    <textarea
                      id="enq-msg"
                      data-testid="enquiry-input-message"
                      className="field resize-none"
                      rows={4}
                      placeholder="Number of events, venue, what matters most to you…"
                      value={form.message}
                      onChange={set("message")}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="enquiry-submit-btn"
                    disabled={sending}
                    className="w-full bg-gold text-ink text-[13px] font-semibold uppercase tracking-[0.22em] py-4.5 py-4 hover:bg-gold-hover transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-3"
                  >
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      "Send Enquiry"
                    )}
                  </button>
                  <p className="text-center text-[11px] text-ivory-mute tracking-wide">
                    We reply within a few hours — usually much faster.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
